import { useState } from "react";

import { toDate, toRelativeDate, isSameDate, toISODateTime } from "common/utils/dates";
import { Feature, UserSubscriptionAction } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Calendar from "client/common/components/calendars/Calendar";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useFeatures } from "client/clinics/hooks";

import { useUpdateSubscriptionMutation } from "../../hooks";
import { DetailSubscription, ListSubscription } from "../../types";
import { minSelectableDeliveryDate } from "../../utils";
import SubscriptionDateBanners from "../SubscriptionDateBanners";

import styles from "../../styles/SubscriptionNextOrderDateUpdateDrawer.module.less";

export type SubscriptionNextOrderDateUpdateDrawerProps = {
  subscription: DetailSubscription | ListSubscription;
};

const SubscriptionNextOrderDateUpdateDrawer = ({
  subscription,
}: SubscriptionNextOrderDateUpdateDrawerProps) => {
  const { close } = useCurrentDrawer();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];
  const {
    mutate: updateSubscription,
    isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  // v2 subscriptions are scheduled by expected delivery date; v1 by next order date.
  const currentDateSource = isEdbEnabled
    ? subscription.expected_delivery_by
    : subscription.next_order_date;
  const currentDate = currentDateSource ? new Date(currentDateSource) : new Date();
  const [scheduleDate, setScheduleDate] = useState<Date>(() => {
    if (isEdbEnabled && currentDate < minSelectableDeliveryDate()) {
      return minSelectableDeliveryDate();
    }
    return currentDate;
  });
  const isSameDateSelected = isSameDate(currentDate, scheduleDate);

  const getDescription = () => {
    if (isEdbEnabled) {
      return subscription.expected_delivery_by
        ? `Use the calendar below to select your next estimated delivery date. Currently, your next estimated delivery is ${toDate(
            subscription.expected_delivery_by,
          )} (${toRelativeDate(subscription.expected_delivery_by)}).`
        : "Use the calendar below to select your next estimated delivery date.";
    }
    return subscription.next_order_date
      ? `Use the calendar below to select your next order date. Currently, you will be receiving this item on ${toDate(
          subscription.next_order_date,
        )} (${toRelativeDate(subscription.next_order_date)}).`
      : "Use the calendar below to select your next order date.";
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title={isEdbEnabled ? "Update next delivery" : "Update next order"}
        description={getDescription()}
        onClose={close}
      />
      <DrawerLayout.Form
        submitText={isEdbEnabled ? "Update delivery" : "Update order"}
        canSubmit={!isSameDateSelected}
        onSubmit={() =>
          updateSubscription({
            action: UserSubscriptionAction.Modify,
            ...(isEdbEnabled
              ? { expected_delivery_by: toISODateTime(scheduleDate) }
              : { next_order_date: toISODateTime(scheduleDate) }),
          })
        }
        isLoading={isLoading}
        error={error}
        fieldErrors={[
          error?.errors.action,
          isEdbEnabled ? error?.errors.expected_delivery_by : error?.errors.next_order_date,
        ]}
      >
        {isEdbEnabled && (
          <div className={styles.disclaimer}>
            Earliest available delivery: {toDate(minSelectableDeliveryDate())}
          </div>
        )}
        <DrawerLayout.Form.Group>
          <Calendar
            disabledDays={{ before: isEdbEnabled ? minSelectableDeliveryDate() : new Date() }}
            selectedDays={scheduleDate}
            modifiers={{ previous: currentDate }}
            onSelect={setScheduleDate}
          />
        </DrawerLayout.Form.Group>
        {!isSameDateSelected && (
          <DrawerLayout.Form.Group>
            <DrawerLayout.Form.Label>
              {isEdbEnabled
                ? "Your next estimated delivery will now be:"
                : "Your next order date will now be:"}
            </DrawerLayout.Form.Label>
            <SubscriptionDateBanners dates={[scheduleDate]} />
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionNextOrderDateUpdateDrawer;
